import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { convertPrice } from "../../../../services/currencyService";
import { FaHotel, FaCalendarAlt, FaBed } from "react-icons/fa";
import { SafetyOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Spin } from "antd";

import SkeletonLoader from "../../../../Components/SkeletonLoader/SkeletonLoader";

import "./PaymentSummary.scss";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { useAppContext } from "../../../../contexts/AppContext";
import { get_depret_from_result } from "../../../../utils/LDSHelpers";
import { getTripGuarantyQuote } from "../../../../services/fhpService";
import translateLDSDestinations from "../../../../services/translateLDSDestinations";
import { formatUsdDisplayInteger } from "../../../../utils/ceilUsd";
import { sendPricingAuditEvent } from "../../../../utils/sendPricingAuditEvent";
import { resolveVacationPackageIncludedBaggage } from "../../../../utils/resolveVacationPackageIncludedBaggage";

const PaymentSummary = ({ isCustomerLink = false }) => {
  const { 
    ldsSupplement, 
    ourPrice, 
    ilsToUsd, 
    selectedResult, 
    roomsAndPaxInfo, 
    tripGuarantySelected, 
    tripGuarantyQuote,
    setTripGuarantySelected,
    setTripGuarantyQuote,
    setCurrentStageIndex 
  } = useSelectedResult();
  const navigate = useNavigate();
  const [addingInsurance, setAddingInsurance] = useState(false);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const { getCreditCardFeePercent, isGlobalConstantLoaded, isAppLoggedIn } = useAppContext();

  const { t } = useTranslation();

  const vpBaggageRecap = useMemo(() => {
    if (!selectedResult?.linkedFlights) return null;
    return resolveVacationPackageIncludedBaggage({
      linkedFlights: selectedResult.linkedFlights,
      fullDeal: selectedResult,
    });
  }, [selectedResult?.linkedFlights, selectedResult?.dealId, selectedResult?.ID]);

  useEffect(() => {
    const p =
      ourPrice?.priceWithCommissionAndCreditCardFee ?? ourPrice?.priceWithCreditCardFee;
    if (p == null || !Number.isFinite(Number(p)) || Number(p) <= 0) return;
    const dealId = selectedResult?.dealId || selectedResult?.id;
    sendPricingAuditEvent({
      page_key: "fe_payment_summary",
      component_key: "PaymentSummary",
      final_price_displayed: Number(p),
      entity_type: "package",
      entity_id: dealId != null ? String(dealId) : undefined,
      surface: isCustomerLink ? "b2c_site" : "agent_site",
      pricing_source: "fe_ingest",
    });
  }, [
    isCustomerLink,
    ourPrice?.priceWithCommissionAndCreditCardFee,
    ourPrice?.priceWithCreditCardFee,
    selectedResult?.dealId,
    selectedResult?.id,
  ]);

  // Helper function to get quote data (extracted for reuse)
  const getQuoteData = useMemo(() => {
    if (!selectedResult || !ourPrice) return null;
    
    // Get flight information
    const linkedFlights = selectedResult.linkedFlights || selectedResult.flight;
    const flight = linkedFlights?.flight?.[0];
    const leg = flight?.leg?.[0];
    const airline_iata = leg?.airline?.code || "LY";
    
    // Get hotel information
    const hotel = selectedResult.hotel || selectedResult.deal?.hotel;
    
    // Get check-in date
    const flightDepDate = get_depret_from_result(selectedResult)?.depDate;
    
    // Helper function to convert date to YYYY-MM-DD format
    const convertToYYYYMMDD = (dateStr) => {
      if (!dateStr) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          if (parts[0].length <= 2 && parts[2].length === 4) {
            return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
          } else if (parts[0].length === 4) {
            return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
          }
        }
      }
      
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      } catch (e) {
        console.error('Error parsing date:', e);
      }
      return null;
    };
    
    let checkInDate = null;
    if (selectedResult?.ourSavedLDSOrder?.dep_date) {
      checkInDate = convertToYYYYMMDD(selectedResult.ourSavedLDSOrder.dep_date);
    } else if (hotel?.checkInDate || hotel?.checkIn) {
      checkInDate = convertToYYYYMMDD(hotel.checkInDate || hotel.checkIn);
    } else if (selectedResult?.searchPhrase?.checkIn) {
      checkInDate = convertToYYYYMMDD(selectedResult.searchPhrase.checkIn);
    } else if (flightDepDate) {
      checkInDate = convertToYYYYMMDD(flightDepDate);
    }
    
    // Get country code
    let destinationCode = "US";
    if (hotel?.hotelCity) {
      const cityTranslation = translateLDSDestinations(hotel.hotelCity);
      if (cityTranslation && typeof cityTranslation === 'object' && cityTranslation.COUNTRYCODE) {
        destinationCode = cityTranslation.COUNTRYCODE;
      } else if (hotel?.hotelCountry) {
        destinationCode = hotel.hotelCountry;
      }
    } else if (hotel?.hotelCountry) {
      destinationCode = hotel.hotelCountry;
    }
    
    // Calculate base trip cost (without insurance)
    let baseTripCost = ourPrice?.priceWithCreditCardFee || ourPrice?.priceWithHandleFee || ourPrice?.priceWithCommission || 0;
    if (tripGuarantySelected && tripGuarantyQuote && tripGuarantyQuote.price) {
      baseTripCost = baseTripCost - parseFloat(tripGuarantyQuote.price);
    }
    
    return {
      airline_iata,
      destination_code: destinationCode,
      trip_cost: baseTripCost,
      flight_departure_date: checkInDate,
      extra_cost: 0,
      currency: "ILS",
      department_id: 1,
      deal_id: selectedResult?.dealId || selectedResult?.id
    };
  }, [selectedResult, ourPrice, tripGuarantySelected, tripGuarantyQuote]);

  // Fetch preview quote when component mounts (if insurance not selected)
  useEffect(() => {
    if (!tripGuarantySelected && getQuoteData && ourPrice && ourPrice.priceWithCreditCardFee > 0) {
      const fetchPreviewQuote = async () => {
        setLoadingPreview(true);
        try {
          const response = await getTripGuarantyQuote(getQuoteData);
          const quoteData = response?.data || response;
          
          if (quoteData && quoteData.result === 0) {
            setPreviewQuote({
              ...quoteData,
              trip_cost: getQuoteData.trip_cost
            });
          }
        } catch (err) {
          console.error('[PaymentSummary] Error fetching preview quote:', err);
        } finally {
          setLoadingPreview(false);
        }
      };
      
      fetchPreviewQuote();
    }
  }, [tripGuarantySelected, getQuoteData, ourPrice]);

  // Function to add Trip Guaranty insurance directly from payment summary
  const handleAddTripGuaranty = async () => {
    if (!selectedResult) {
      console.error('[PaymentSummary] No selectedResult available');
      return;
    }

    if (!getQuoteData) {
      console.error('[PaymentSummary] No quote data available');
      return;
    }

    setAddingInsurance(true);
    
    try {
      // Get quote
      const response = await getTripGuarantyQuote(getQuoteData);
      
      const quoteData = response?.data || response;
      
      if (quoteData && quoteData.result === 0) {
        const updatedQuote = {
          ...quoteData,
          trip_cost: getQuoteData.trip_cost
        };
        
        // Add insurance to context
        setTripGuarantyQuote(updatedQuote);
        setTripGuarantySelected(true);
        setPreviewQuote(null); // Clear preview quote
        
        // The total will be recalculated automatically via calculateLdsTotalPayment
      } else {
        const errorMessage = quoteData?.hebrew_message || quoteData?.message || "לא ניתן לקבל הצעת מחיר";
        console.error('[PaymentSummary] Failed to get Trip Guaranty quote:', errorMessage);
        // You could show a notification here if needed
      }
    } catch (err) {
      console.error('[PaymentSummary] Error adding Trip Guaranty:', err);
    } finally {
      setAddingInsurance(false);
    }
  };

  // Helper function to parse roomSet to human-readable format
  const parseRoomSet = (roomSet) => {
    if (!roomSet) return null;
    
    // Match patterns like A2C3, A5, P5
    const match = roomSet.match(/A(\d+)(?:C(\d+))?|P(\d+)/);
    if (match) {
      let adults = 0;
      let kids = 0;
      
      if (match[3]) { // P format: P{total}
        const total = parseInt(match[3]);
        adults = 2; // Default to 2 adults
        kids = Math.max(0, total - 2);
      } else if (match[1]) { // A{adults}C{kids} or A{adults}
        adults = parseInt(match[1]);
        kids = match[2] ? parseInt(match[2]) : 0;
      }
      
      if (adults >= 1 && adults <= 4 && kids >= 0 && kids <= 5) {
        let label = "";
        if (adults === 2 && kids === 0) {
          label = "זוג";
        } else if (adults === 2 && kids > 0) {
          label = `זוג + ${kids}`;
        } else if (adults === 1 && kids === 0) {
          label = "מבוגר אחד";
        } else if (adults === 1 && kids > 0) {
          label = `מבוגר אחד + ${kids} ילדים`;
        } else if (kids === 0) {
          label = `${adults} מבוגרים`;
        } else {
          label = `${adults} מבוגרים + ${kids} ילדים`;
        }
        return label;
      }
    }
    return null;
  };

  // Get booking details for customer summary
  const bookingDetails = useMemo(() => {
    if (!isCustomerLink || !selectedResult) return null;
    
    const { hotel } = selectedResult;
    const { hotelName, hotelCity } = hotel || {};
    const { depDate, retDate } = get_depret_from_result(selectedResult);
    
    // Get room types from selected rooms
    const roomTypes = roomsAndPaxInfo?.map((roomInfo, index) => {
      const room = roomInfo?.room;
      if (room?.roomType) {
        const roomSet = room?.roomSet || '';
        const composition = parseRoomSet(roomSet);
        return {
          roomType: room.roomType,
          composition: composition
        };
      }
      return null;
    }).filter(Boolean) || [];
    
    return {
      hotelName: hotelName || "לא זמין",
      hotelCity: hotelCity || "",
      depDate: depDate || "",
      retDate: retDate || "",
      roomTypes: roomTypes.length > 0 ? roomTypes : [{ roomType: "לא נבחר", composition: null }]
    };
  }, [isCustomerLink, selectedResult, roomsAndPaxInfo]);

  const supplementSummary = () => {
    // Convert the dictionary to an array
    const supplementArray = Object.values(ldsSupplement);

    // Filter the array
    const filterSupp = supplementArray.filter((supp) => supp.checked);
    
    // Helper function to translate supplement type and add weight
    const getSupplementLabel = (supp) => {
      const type = supp.type || '';
      let typeLabel = '';
      
      if (type === 'LUGGAGE' || type === 'BAGGAGE') {
        typeLabel = 'מזוודות';
      } else if (type === 'TROLLEY') {
        typeLabel = 'תיקי יד';
      } else {
        // Try to get from translation or use description
        typeLabel = t(supp.type) || supp.description || type;
      }
      
      // Get weight from multiple sources
      let weight = '';
      if (supp.weightText) {
        weight = supp.weightText;
      } else if (supp.weight && supp.unit) {
        weight = `${supp.weight}${supp.unit}`;
      } else if (supp.orig?.supp?.params) {
        const weightParam = supp.orig.supp.params.find(p => p.name === 'weight');
        const unitParam = supp.orig.supp.params.find(p => p.name === 'unit');
        if (weightParam && unitParam) {
          weight = `${weightParam.value}${unitParam.value}`;
        }
      }
      
      return weight ? `${typeLabel} ${weight}` : typeLabel;
    };
    
    // Helper function to get flight direction
    const getFlightDirection = (supp) => {
      // Check fltRef first - "-2" or "-3" means round trip (הלוך וחזור)
      const fltRef = supp.flight?.fltRef || supp.orig?.supp?.fltRef || supp.fltRef;
      if (fltRef === "-2" || fltRef === "-3") {
        return " (טיסה הלוך וחזור)";
      }
      
      // If specific flight, show direction
      if (supp.flight) {
        const depCity = supp.flight.depCity || '';
        const arrCity = supp.flight.arrCity || '';
        if (depCity && arrCity) {
          return ` (${depCity} → ${arrCity})`;
        }
      } else if (supp.orig?.flight) {
        const leg = Array.isArray(supp.orig.flight) ? supp.orig.flight[0] : supp.orig.flight;
        const depCity = leg?.depCity || '';
        const arrCity = leg?.arrCity || '';
        if (depCity && arrCity) {
          return ` (${depCity} → ${arrCity})`;
        }
      }
      return '';
    };
    
    return filterSupp.map((s, index) => {
      const flightDirection = getFlightDirection(s);
      
      // Calculate USD price from ILS
      const priceInIls = s.ourFare || 0;
      const priceInUsd = ilsToUsd ? ilsToUsd(priceInIls) : 0;
      const priceDisplay = priceInUsd > 0 
        ? `${convertPrice(priceInIls)} (${formatUsdDisplayInteger(priceInUsd)}$)`
        : convertPrice(priceInIls);
      
      return (
        <div key={index} className="ci-summery-fee1-wrapper">
          <label className="ci-summery-fee1-title">
            {getSupplementLabel(s)}{flightDirection}
          </label>
          <label id="price-commission" className="ci-summery-fee0-value">
            {priceDisplay}
          </label>
        </div>
      );
    });
  };

  const displayCreditCardFee = () => {
    return (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: "4px 0",
          }}
        >
          <div className={"ci-summery-fee1-title"}>
            עמלת כרטיס ({getCreditCardFeePercent()}%)
          </div>
          <div className={"ci-summery-fee2-value"}>
            {convertPrice(ourPrice.creditCardFee || 0)}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className={`payment-summery-wrapper ${isCustomerLink ? 'customer-payment-summary' : ''}`}>
        {/* Back to Package Button */}
        <button
          type="button"
          className="back-to-package-button"
          onClick={() => {
            if (setCurrentStageIndex) {
              setCurrentStageIndex(0);
            } else {
              const currentPath = window.location.pathname;
              const basePath = currentPath.split('/').slice(0, -1).join('/');
              navigate(`${basePath}/details`);
            }
          }}
        >
          <ArrowRightOutlined className="back-to-package-icon" />
          <span>חזרה לחבילה</span>
        </button>
        
        <div className="payment-agent-price-payment-wrapper">
          <label>{t("ordersummery.ordersummery")}</label>
        </div>
        
        {/* Booking Details for Customer */}
        {isCustomerLink && bookingDetails && (
          <div className="customer-booking-details">
            <div className="booking-detail-item">
              <FaHotel className="booking-detail-icon" />
              <div className="booking-detail-content">
                <div className="booking-detail-label">מלון</div>
                <div className="booking-detail-value">{bookingDetails.hotelName}</div>
                {bookingDetails.hotelCity && (
                  <div className="booking-detail-subvalue">{bookingDetails.hotelCity}</div>
                )}
              </div>
            </div>
            
            <div className="booking-detail-item">
              <FaCalendarAlt className="booking-detail-icon" />
              <div className="booking-detail-content">
                <div className="booking-detail-label">תאריכים</div>
                <div className="booking-detail-value">
                  {bookingDetails.depDate} - {bookingDetails.retDate}
                </div>
              </div>
            </div>
            
            <div className="booking-detail-item">
              <FaBed className="booking-detail-icon" />
              <div className="booking-detail-content">
                <div className="booking-detail-label">חדרים</div>
                <div className="booking-detail-value">
                  {bookingDetails.roomTypes.map((room, index) => (
                    <div key={index} className="room-type-badge-wrapper">
                      <span className="room-type-badge">
                        {room.roomType}
                      </span>
                      {room.composition && (
                        <span className="room-composition-badge">
                          הרכב חדר: {room.composition}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {vpBaggageRecap?.summaryLabel ? (
          <div className="vp-payment-summary-baggage" dir="rtl" role="status">
            <span className="vp-payment-summary-baggage__ico" aria-hidden>
              ✈
            </span>
            <div>
              <div className="vp-payment-summary-baggage__label">כבודה כלולה בכרטיס</div>
              <div className="vp-payment-summary-baggage__value">{vpBaggageRecap.summaryLabel}</div>
            </div>
          </div>
        ) : null}
        
        <div className="ci-summery-content-wrapper">
          {/* Fees */}
          <div className="ci-summery-fee1-wrapper">
            <label className="ci-summery-fee1-title">מחיר החבילה</label>
            <label id="price-commission" className="ci-summery-fee1-value">
              {(() => {
                const packagePrice = !isAppLoggedIn()
                  ? ourPrice.priceWithCommission + ourPrice.handleFee
                  : ourPrice.priceWithCommission;
                return ilsToUsd
                  ? `${convertPrice(packagePrice)} (${formatUsdDisplayInteger(Number(ilsToUsd(Number(packagePrice))))}$)`
                  : convertPrice(packagePrice);
              })()}
            </label>
          </div>
          
          {/* Room prices breakdown */}
          {roomsAndPaxInfo && roomsAndPaxInfo.length > 0 && (
            <div className="room-prices-breakdown">
              {roomsAndPaxInfo.map((roomInfo, index) => {
                const ourTotalFare = roomInfo?.room?.ourFareDetails?.ourTotalFare;
                if (!ourTotalFare) return null;
                
                // Calculate room price - use priceWithCommissionAndCreditCardFee if available (includes handling fee + credit card fee)
                // Otherwise use priceWithHandleFee (includes handling fee but not credit card fee)
                // Only fall back to priceWithCommission if neither exists
                let roomPriceValue = 0;
                if (!isAppLoggedIn()) {
                  // For customers: use priceWithCommissionAndCreditCardFee (includes handling fee + credit card fee)
                  // Or priceWithHandleFee (includes handling fee)
                  roomPriceValue = ourTotalFare.priceWithCommissionAndCreditCardFee || 
                                  ourTotalFare.priceWithHandleFee || 
                                  (ourTotalFare.priceWithCommission || 0) + (ourTotalFare.handleFee || 0);
                } else {
                  // For agents: use priceWithHandleFee (includes handling fee) or priceWithCommission
                  roomPriceValue = ourTotalFare.priceWithHandleFee || 
                                  (ourTotalFare.priceWithCommission || 0) + (ourTotalFare.handleFee || 0);
                }
                
                if (roomPriceValue === 0) return null;
                
                const roomType = roomInfo?.room?.roomType || `חדר ${index + 1}`;
                const roomSet = roomInfo?.room?.roomSet || '';
                const roomComposition = parseRoomSet(roomSet);
                
                return (
                  <div key={index} className="ci-summery-fee1-wrapper room-price-item">
                    <div className="room-price-item-content">
                      <div className="ci-summery-fee1-title">
                        {roomType}
                      </div>
                      {roomComposition && (
                        <div className="room-composition-label">
                          הרכב חדר: {roomComposition}
                        </div>
                      )}
                    </div>
                    <label id="price-commission" className="ci-summery-fee2-value">
                      {ilsToUsd
                        ? `${convertPrice(roomPriceValue)} (${formatUsdDisplayInteger(Number(ilsToUsd(Number(roomPriceValue))))}$)`
                        : convertPrice(roomPriceValue)}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
          
          {supplementSummary()}
          
          {/* Trip Guaranty Insurance */}
          {tripGuarantySelected && tripGuarantyQuote && tripGuarantyQuote.price > 0 && (
            <div className="ci-summery-fee1-wrapper">
              <label className="ci-summery-fee1-title">ביטוח נסיעות Trip Guaranty</label>
              <label id="price-commission" className="ci-summery-fee1-value">
                {ilsToUsd
                  ? `${convertPrice(tripGuarantyQuote.price)} (${formatUsdDisplayInteger(Number(ilsToUsd(Number(tripGuarantyQuote.price))))}$)`
                  : convertPrice(tripGuarantyQuote.price)}
              </label>
            </div>
          )}
          
          {isAppLoggedIn() && (
            <div className="ci-summery-fee1-wrapper">
              <label className="ci-summery-fee1-title">עמלת HR</label>
              <label id="price-commission" className="ci-summery-fee1-value">
                {convertPrice(ourPrice.agentCommission)}
              </label>
            </div>
          )}
          {isAppLoggedIn() && (
            <div className="ci-summery-fee1-wrapper">
              <label className="ci-summery-fee1-title">
                {t("paymentagent.handlingFee")}
              </label>

              <label id="price-commission" className="ci-summery-fee2-value">
                {!isGlobalConstantLoaded ? (
                  <SkeletonLoader height={10} style={{ width: 50 }} />
                ) : ilsToUsd
                  ? `${convertPrice(ourPrice.handleFee)} (${formatUsdDisplayInteger(Number(ilsToUsd(Number(ourPrice.handleFee))))}$)`
                  : convertPrice(ourPrice.handleFee)}
              </label>
            </div>
          )}
          {displayCreditCardFee()}
        </div>
        <div></div>
        <div className="ci-summery-border"></div>
        
        {/* Trip Guaranty Promotion Block - אם אין ביטוח */}
        {!tripGuarantySelected && (
          <div className="trip-guaranty-promotion-block">
            <div className="trip-guaranty-promotion-icon">
              <SafetyOutlined />
            </div>
            <div className="trip-guaranty-promotion-content">
              <h4 className="trip-guaranty-promotion-title">
                הגן על הנסיעה שלך עם ביטוח נסיעות Trip Guaranty
              </h4>
              <p className="trip-guaranty-promotion-text">
                מומלץ בחום להוסיף ביטוח נסיעות להגנה מקסימלית על הנסיעה שלך. ביטוח Trip Guaranty מספק כיסוי מקיף לכל הנסיעה.
              </p>
              <div className="trip-guaranty-promotion-price-button-wrapper">
                {loadingPreview ? (
                  <div className="trip-guaranty-promotion-price">
                    <Spin size="small" style={{ marginLeft: 8 }} />
                    <span>מחשב מחיר...</span>
                  </div>
                ) : previewQuote && previewQuote.price ? (
                  <div className="trip-guaranty-promotion-price">
                    <span className="trip-guaranty-promotion-price-label">מחיר ביטוח:</span>
                    <span className="trip-guaranty-promotion-price-value">
                      {ilsToUsd
                        ? `${convertPrice(previewQuote.price)} (${formatUsdDisplayInteger(Number(ilsToUsd(Number(previewQuote.price))))}$)`
                        : convertPrice(previewQuote.price)}
                    </span>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="trip-guaranty-promotion-button"
                  onClick={handleAddTripGuaranty}
                  disabled={addingInsurance || loadingPreview}
                >
                  {addingInsurance ? (
                    <>
                      <Spin size="small" style={{ marginLeft: 8 }} />
                      טוען...
                    </>
                  ) : (
                    <>
                      <SafetyOutlined style={{ marginLeft: 8, fontSize: 18 }} />
                      הוסף ביטוח נסיעות
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        <div className="splitPayment-card-amount-totalprice ci-summery-topay-wrapper">
          <label className="splitPayment-card-amount-price-title ci-summery-topay">
            {t("paymentagent.priceis")}:
            <span
              id="priproblem-total"
              className="splitPayment-card-amount-price-number"
            >
              {convertPrice(ourPrice.priceWithCreditCardFee)} (
              {formatUsdDisplayInteger(
                Number(
                  ilsToUsd(Number(ourPrice.priceWithCreditCardFee))
                )
              )}
              $)
            </span>
          </label>
        </div>
      </div>
    </>
  );
};

export default PaymentSummary;
